import type {
  PullRequestAction,
  PullRequestDetail,
  PullRequestMergeMethod,
  PullRequestUpdateMethod,
} from "@cmmd-forge/contracts";

import { useState } from "react";

import { Button } from "~/components/ui/button";
import type { PullRequestAgentSelection } from "~/pullRequest/pullRequestSelectionContext";
import {
  MERGE_METHOD_LABELS,
  PullRequestActionsMenu,
  pullRequestActionLabel,
} from "./PullRequestActionsMenu";
import {
  PullRequestActionConfirmation,
  requiresPullRequestActionConfirmation,
} from "./PullRequestActionConfirmation";

export interface PullRequestActionsProps {
  readonly actions: ReadonlyArray<PullRequestAction>;
  readonly detail: Pick<
    PullRequestDetail,
    | "autoMergeEnabled"
    | "capabilities"
    | "isDraft"
    | "mergeability"
    | "provider"
    | "state"
    | "url"
    | "viewerPermissions"
  >;
  readonly selection: PullRequestAgentSelection | null;
  readonly mergeMethods: ReadonlyArray<PullRequestMergeMethod>;
  readonly updateMethods: ReadonlyArray<PullRequestUpdateMethod>;
  readonly mergeMethod: PullRequestMergeMethod;
  readonly updateMethod: PullRequestUpdateMethod;
  readonly onMergeMethodChange: (value: PullRequestMergeMethod) => void;
  readonly onUpdateMethodChange: (value: PullRequestUpdateMethod) => void;
  readonly pending: boolean;
  readonly error: Error | null;
  readonly onRun: (action: PullRequestAction) => void;
  readonly refreshing: boolean;
  readonly onRefresh: () => Promise<void>;
}

const PRIMARY_ACTIONS: ReadonlyArray<PullRequestAction> = ["ready", "merge", "update-branch"];

type PullRequestActionState = Pick<PullRequestDetail, "autoMergeEnabled" | "isDraft" | "state">;

const ACTION_STATE_GUARDS: Partial<
  Readonly<Record<PullRequestAction, (detail: PullRequestActionState) => boolean>>
> = {
  close: (detail) => detail.state === "open",
  reopen: (detail) => detail.state === "closed",
  ready: (detail) => detail.state === "open" && detail.isDraft,
  draft: (detail) => detail.state === "open" && !detail.isDraft,
  "enable-auto-merge": (detail) =>
    detail.state === "open" && !detail.isDraft && detail.autoMergeEnabled !== true,
  "disable-auto-merge": (detail) => detail.state === "open" && detail.autoMergeEnabled === true,
};

export function PullRequestActions(props: PullRequestActionsProps) {
  const [confirmationAction, setConfirmationAction] = useState<PullRequestAction | null>(null);
  const actions = props.actions.filter((action) => canRunAction(action, props.detail));
  const primaryAction = PRIMARY_ACTIONS.find((action) => actions.includes(action)) ?? null;
  const secondaryActions = actions.filter((action) => action !== primaryAction);
  const showsMergeMethods =
    includesAnyAction(actions, ["merge", "enable-auto-merge"]) && props.mergeMethods.length > 1;
  const showsUpdateMethods = actions.includes("update-branch") && props.updateMethods.length > 1;
  const requestRun = (action: PullRequestAction) => {
    if (requiresPullRequestActionConfirmation(action)) {
      setConfirmationAction(action);
      return;
    }
    props.onRun(action);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5">
        {primaryAction !== null ? (
          <PrimaryPullRequestAction
            action={primaryAction}
            mergeMethod={props.mergeMethod}
            pending={props.pending}
            onRun={requestRun}
          />
        ) : null}
        <PullRequestActionsMenu
          actions={secondaryActions}
          mergeMethod={props.mergeMethod}
          mergeMethods={props.mergeMethods}
          updateMethod={props.updateMethod}
          updateMethods={props.updateMethods}
          pending={props.pending}
          showMergeMethods={showsMergeMethods}
          showUpdateMethods={showsUpdateMethods}
          onMergeMethodChange={props.onMergeMethodChange}
          onUpdateMethodChange={props.onUpdateMethodChange}
          onRun={requestRun}
          refreshing={props.refreshing}
          onRefresh={props.onRefresh}
          detail={props.detail}
          selection={props.selection}
        />
      </div>
      <PullRequestActionFeedback hasActions={actions.length > 0} error={props.error} />
      <PullRequestActionConfirmation
        action={confirmationAction}
        mergeMethod={props.mergeMethod}
        pending={props.pending}
        onCancel={() => setConfirmationAction(null)}
        onConfirm={() => {
          if (confirmationAction === null) return;
          props.onRun(confirmationAction);
          setConfirmationAction(null);
        }}
      />
    </div>
  );
}

function PrimaryPullRequestAction(props: {
  readonly action: PullRequestAction;
  readonly mergeMethod: PullRequestMergeMethod;
  readonly pending: boolean;
  readonly onRun: (action: PullRequestAction) => void;
}) {
  return (
    <Button
      type="button"
      size="xs"
      disabled={props.pending}
      onClick={() => props.onRun(props.action)}
    >
      {props.pending ? "Working…" : primaryActionLabel(props.action, props.mergeMethod)}
    </Button>
  );
}

function PullRequestActionFeedback({
  hasActions,
  error,
}: {
  readonly hasActions: boolean;
  readonly error: Error | null;
}) {
  return (
    <>
      {hasActions ? null : (
        <p className="text-xs text-muted-foreground">
          No provider actions are available with the current permissions.
        </p>
      )}
      {error !== null ? (
        <p className="mt-2 text-sm text-destructive-foreground">Action failed: {error.message}</p>
      ) : null}
    </>
  );
}

function primaryActionLabel(
  action: PullRequestAction,
  mergeMethod: PullRequestMergeMethod,
): string {
  return action === "merge" ? MERGE_METHOD_LABELS[mergeMethod] : pullRequestActionLabel(action);
}

function canRunAction(
  action: PullRequestAction,
  detail: Pick<
    PullRequestDetail,
    "autoMergeEnabled" | "capabilities" | "isDraft" | "state" | "viewerPermissions"
  >,
): boolean {
  return (
    isActionValidForPullRequestState(action, detail) &&
    detail.capabilities.actions.includes(action) &&
    detail.viewerPermissions.actions.includes(action)
  );
}

function isActionValidForPullRequestState(
  action: PullRequestAction,
  detail: PullRequestActionState,
): boolean {
  const guard = ACTION_STATE_GUARDS[action];
  return guard ? guard(detail) : detail.state === "open";
}

function includesAnyAction(
  actions: ReadonlyArray<PullRequestAction>,
  candidates: ReadonlyArray<PullRequestAction>,
): boolean {
  return candidates.some((candidate) => actions.includes(candidate));
}
